import { Injectable, OnDestroy } from '@angular/core';

interface RedirectMarker {
  connectionId: string;
  paymentId: string;
  documentStartedAt: number;
  returned: boolean;
}

const storageKey = (flowId: string) => `payever_checkout_santander_at_browser_back.${flowId}`;

function hasReturned(marker: RedirectMarker): boolean {
  const navigation = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming;

  return !!marker && (marker.returned
    || (navigation?.type === 'back_forward' && marker.documentStartedAt !== performance.timeOrigin));
}

@Injectable({ providedIn: 'root' })
export class PaymentBrowserBackStorage implements OnDestroy {
  private readonly markers = new Map<string, RedirectMarker>();
  private readonly onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      this.markers.forEach((marker, flowId) => this.save(flowId, { ...marker, returned: true }));
    }
  };

  constructor() {
    window.addEventListener('pageshow', this.onPageShow);
  }

  ngOnDestroy(): void {
    window.removeEventListener('pageshow', this.onPageShow);
  }

  remember(flowId: string, connectionId: string, paymentId: string): void {
    if (paymentId) {
      this.save(flowId, {
        connectionId, paymentId, documentStartedAt: performance.timeOrigin, returned: false,
      });
    }
  }

  hasReturned(flowId: string, connectionId: string, paymentId: string): boolean {
    const marker = this.get(flowId);
    if (marker && marker.connectionId === connectionId && marker.paymentId === paymentId && hasReturned(marker)) {
      this.save(flowId, { ...marker, returned: true });

      return true;
    }

    return false;
  }

  clear(flowId: string): void {
    this.markers.delete(flowId);
    try {
      sessionStorage.removeItem(this.key(flowId));
    } catch {}
  }

  private get(flowId: string): RedirectMarker {
    let marker = this.markers.get(flowId);
    try {
      marker = JSON.parse(sessionStorage.getItem(this.key(flowId))) || marker;
    } catch {}
    if (marker) {
      this.markers.set(flowId, marker);
    }

    return marker;
  }

  private save(flowId: string, marker: RedirectMarker): void {
    this.markers.set(flowId, marker);
    try {
      sessionStorage.setItem(this.key(flowId), JSON.stringify(marker));
    } catch {}
  }

  private key(flowId: string): string {
    return storageKey(flowId);
  }
}
